/**
 * Wizard-Form styles for Shadow DOM
 *
 * Defines all CSS custom properties (design tokens) on :host
 * These properties pierce Shadow DOM and are available to all child components.
 */

import { css } from 'lit';

import { sharedAnimations } from '../styles/shared.styles.js';

export const wizardFormStyles = [
  sharedAnimations,
  css`
  /* ----------------------------------------
     :host - Design tokens & base styles
     ---------------------------------------- */
  :host {
    display: block;

    /* Color tokens */
    --wf-color-primary: #8040f0;
    --wf-color-primary-border: #602cbb;
    --wf-color-primary-light: rgba(128, 64, 240, 0.08);
    --wf-color-surface: #f3f3f5;
    --wf-color-surface-hover: #e9e9eb;
    --wf-color-border: #d4d4d4;
    --wf-color-text: #0a0a0a;
    --wf-color-text-secondary: #404040;
    --wf-color-text-muted: #646464;
    --wf-color-error: #dc3545;
    --wf-color-error-light: rgba(220, 53, 69, 0.1);
    --wf-color-badge-bg: #fcfcfc;
    --wf-color-badge-border: #d4d4d4;
    --wf-color-badge-text: #5f5f5f;
    --wf-color-progress-active: rgba(0, 0, 0, 0.1);
    --wf-color-progress-inactive: rgba(0, 0, 0, 0.05);

    /* Focus ring */
    --wf-focus-ring-width: 3px;
    --wf-focus-ring-primary: rgba(128, 64, 240, 0.2);
    --wf-focus-ring-error: rgba(220, 53, 69, 0.2);

    /* Spacing tokens */
    --wf-spacing-05: 2px;
    --wf-spacing-1: 4px;
    --wf-spacing-2: 8px;
    --wf-spacing-3: 12px;
    --wf-spacing-4: 16px;
    --wf-spacing-5: 20px;
    --wf-spacing-6: 24px;
    --wf-spacing-8: 32px;

    /* Border radius tokens */
    --wf-radius-sm: 4px;
    --wf-radius-md: 8px;
    --wf-radius-lg: 8px;
    --wf-radius-full: 99px;

    /* Typography tokens */
    --wf-font-size-xs: 0.75rem;
    --wf-font-size-sm: 0.875rem;
    --wf-font-size-base: 1rem;
    --wf-font-size-lg: 1.25rem;
    --wf-font-size-xl: 1.5rem;
    --wf-font-size-2xl: 2rem;
    --wf-font-size-3xl: 2.5rem;
    --wf-font-family-mono: ui-monospace, SFMono-Regular, SF Mono, Menlo, Consolas, Liberation Mono, monospace;
    --wf-font-weight-input: 400;
    --wf-font-weight-label: 500;
    --wf-font-weight-heading: 600;
    --wf-font-weight-button: 400;

    /* Input tokens */
    --wf-input-min-height: 56px;
    --wf-textarea-min-height: 100px;

    /* Component dimensions */
    --wf-btn-min-height: 48px;
    --wf-badge-size: 24px;
    --wf-spinner-size: 20px;
    --wf-progress-height: 4px;

    /* Frosted glass effect (iOS-style) */
    --wf-glass-bg: rgba(255, 255, 255, 0.7);
    --wf-glass-bg-hover: rgba(255, 255, 255, 0.85);
    --wf-glass-border: rgba(0, 0, 0, 0.1);
    --wf-glass-blur: 20px;
    --wf-glass-shadow: 0 2px 4px rgba(0, 0, 0, 0.03);
  }

  :host([hidden]) {
    display: none;
  }

  /* Dark theme */
  :host([theme='dark']) {
    --wf-color-surface: #2d2d2d;
    --wf-color-surface-hover: #3d3d3d;
    --wf-color-border: #4d4d4d;
    --wf-color-text: #f8f9fa;
    --wf-color-text-secondary: #d0d0d0;
    --wf-color-text-muted: #adb5bd;
    --wf-color-badge-bg: #3d3d3d;
    --wf-color-badge-border: #4d4d4d;
    --wf-color-badge-text: #adb5bd;
    --wf-color-progress-active: rgba(200, 200, 200, 0.6);
    --wf-color-progress-inactive: rgba(100, 100, 100, 0.6);

    /* Dark theme frosted glass */
    --wf-glass-bg: rgba(45, 45, 45, 0.7);
    --wf-glass-bg-hover: rgba(60, 60, 60, 0.8);
    --wf-glass-border: rgba(255, 255, 255, 0.1);
  }

  /* Auto theme (respects system preference) */
  @media (prefers-color-scheme: dark) {
    :host([theme='auto']) {
      --wf-color-surface: #2d2d2d;
      --wf-color-surface-hover: #3d3d3d;
      --wf-color-border: #4d4d4d;
      --wf-color-text: #f8f9fa;
      --wf-color-text-secondary: #d0d0d0;
      --wf-color-text-muted: #adb5bd;
      --wf-color-badge-bg: #3d3d3d;
      --wf-color-badge-border: #4d4d4d;
      --wf-color-badge-text: #adb5bd;
      --wf-color-progress-active: rgba(200, 200, 200, 0.6);
      --wf-color-progress-inactive: rgba(100, 100, 100, 0.6);

      /* Dark theme frosted glass */
      --wf-glass-bg: rgba(45, 45, 45, 0.7);
      --wf-glass-bg-hover: rgba(60, 60, 60, 0.8);
      --wf-glass-border: rgba(255, 255, 255, 0.1);
    }
  }

  /* ----------------------------------------
     Container
     ---------------------------------------- */
  .wf-container {
    margin: 0 auto;
  }

  /* ----------------------------------------
     Success screen
     ---------------------------------------- */
  .wf-success-screen {
    text-align: center;
    padding: var(--wf-spacing-6);
  }

  /* ----------------------------------------
     Error message
     ---------------------------------------- */
  .wf-form-error {
    padding: var(--wf-spacing-4);
    margin-bottom: var(--wf-spacing-4);
    background-color: var(--wf-color-error-light);
    border: 1px solid var(--wf-color-error);
    border-radius: var(--wf-radius-md);
    color: var(--wf-color-error);
    font-size: var(--wf-font-size-base);
  }

  /* ----------------------------------------
     Slot styles
     ---------------------------------------- */
  ::slotted(wf-step) {
    display: none;
    width: 100%;
  }

  ::slotted(wf-step[active]) {
    display: block;
    animation: wf-stepFadeIn 0.3s ease forwards;
  }

  ::slotted(wf-step[direction='forward'][active]) {
    animation: wf-stepSlideInRight 0.3s ease forwards;
  }

  ::slotted(wf-step[direction='backward'][active]) {
    animation: wf-stepSlideInLeft 0.3s ease forwards;
  }

  ::slotted(wf-step[leaving]) {
    display: block;
    animation: wf-stepFadeOut 0.2s ease forwards;
  }

  /* Success slot hidden by default, shown when submitted */
  ::slotted([slot='success']) {
    display: none;
  }

  :host([submitted]) ::slotted([slot='success']) {
    display: block;
  }

  :host([submitted]) ::slotted(wf-step) {
    display: none !important;
  }
`,
];
